import { useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useSupabaseData } from "./useSupabaseData";

const ALL_TIME_CHUNK_DAYS = 90;
const ALL_TIME_RPC_LIMIT = 600;
const ALL_TIME_MAX_RESULTS = 200;
const DEFAULT_BRAND_FALLBACK = "holistic_hercules";

const toUTCDate = (dateString) =>
  new Date(`${dateString}T00:00:00Z`);

const formatDate = (date) => date.toISOString().slice(0, 10);

const buildChunkRanges = (startDateString, endDateString, chunkSizeDays) => {
  if (!startDateString || !endDateString) {
    return [];
  }

  const ranges = [];
  let cursor = toUTCDate(startDateString);
  const end = toUTCDate(endDateString);

  while (cursor <= end) {
    const chunkStart = new Date(cursor);
    const chunkEnd = new Date(cursor);
    chunkEnd.setUTCDate(chunkEnd.getUTCDate() + chunkSizeDays - 1);

    if (chunkEnd > end) {
      chunkEnd.setTime(end.getTime());
    }

    ranges.push({
      start: formatDate(chunkStart),
      end: formatDate(chunkEnd),
    });

    cursor = new Date(chunkEnd);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return ranges;
};

const getCommissionRateForRoas = (tiers, roas) => {
  if (!tiers || tiers.length === 0 || !Number.isFinite(roas)) {
    return 0;
  }

  const matchingTier =
    tiers.find(
      (tier) =>
        roas >= parseFloat(tier.tier_min_roas) &&
        (tier.tier_max_roas === null ||
          roas < parseFloat(tier.tier_max_roas))
    ) ||
    tiers.reduce((best, tier) => {
      if (!best) return tier;
      return parseFloat(tier.tier_min_roas) >
        parseFloat(best.tier_min_roas)
        ? tier
        : best;
    }, null);

  return matchingTier ? parseFloat(matchingTier.commission_percentage || 0) : 0;
};

const useAffiliateData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use the optimized useSupabaseData hook at the top level
  const { clearCache } = useSupabaseData();

  const fetchCommissionTiersForDate = useCallback(
    async (brandKey, referenceDate) => {
      const effectiveBrand = brandKey || DEFAULT_BRAND_FALLBACK;
      const calcDate =
        referenceDate && referenceDate.length >= 10
          ? referenceDate.slice(0, 10)
          : formatDate(new Date());

      const query = supabase
        .from("commission_tier_config")
        .select(
          "tier_min_roas, tier_max_roas, commission_percentage, effective_from_date, effective_until_date"
        )
        .eq("brand", effectiveBrand)
        .lte("effective_from_date", calcDate)
        .order("tier_min_roas", { ascending: true });

      query.or(
        `effective_until_date.is.null,effective_until_date.gte.${calcDate}`
      );

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      const calcDateUTC = toUTCDate(calcDate);

      return (data || [])
        .filter((tier) => {
          const start = toUTCDate(tier.effective_from_date);
          const end = tier.effective_until_date
            ? toUTCDate(tier.effective_until_date)
            : null;

          if (start > calcDateUTC) return false;
          if (end && end < calcDateUTC) return false;
          return true;
        })
        .sort(
          (a, b) =>
            parseFloat(a.tier_min_roas || 0) - parseFloat(b.tier_min_roas || 0)
        );
    },
    []
  );

  const getDateBounds = useCallback(async (brandFilter) => {
    const buildDateQuery = (ascending) => {
      const query = supabase
        .from("ad_performance")
        .select("date")
        .not("date", "is", null)
        .gt("spend", 0)
        .order("date", { ascending })
        .limit(1);

      if (brandFilter) {
        query.eq("brand", brandFilter);
      }

      return query;
    };

    const [{ data: minData, error: minError }, { data: maxData, error: maxError }] =
      await Promise.all([buildDateQuery(true), buildDateQuery(false)]);

    if (minError) throw minError;
    if (maxError) throw maxError;

    return {
      minDate: minData?.[0]?.date || null,
      maxDate: maxData?.[0]?.date || null,
    };
  }, []);

  const loadAllTimeAffiliateSummary = useCallback(
    async (brandFilter) => {
      const { minDate, maxDate } = await getDateBounds(brandFilter);
      if (!minDate || !maxDate) {
        return [];
      }

    const chunkRanges = buildChunkRanges(
      minDate,
      maxDate,
      ALL_TIME_CHUNK_DAYS
    );

    if (chunkRanges.length === 0) {
      return [];
    }

    const aggregates = new Map();

    for (const range of chunkRanges) {
      const { data, error } = await supabase.rpc(
        "get_affiliate_performance_summary",
        {
          brand_filter: brandFilter,
          start_date: range.start,
          end_date: range.end,
          limit_count: ALL_TIME_RPC_LIMIT,
        }
      );

      if (error) {
        throw error;
      }

      (data || []).forEach((row) => {
        const name = row.affiliate_name || "Unassigned";
        if (!aggregates.has(name)) {
          aggregates.set(name, {
            affiliate_name: name,
            total_ad_spend: 0,
            total_revenue: 0,
            total_profit: 0,
            total_purchases: 0,
            ad_count: 0,
          });
        }

        const entry = aggregates.get(name);
        const spend = parseFloat(row.total_ad_spend) || 0;
        const revenue = parseFloat(row.total_revenue) || 0;
        const profit =
          row.total_profit !== null && row.total_profit !== undefined
            ? parseFloat(row.total_profit) || 0
            : revenue - spend;

        entry.total_ad_spend += spend;
        entry.total_revenue += revenue;
        entry.total_profit += profit;
        entry.total_purchases += parseInt(row.total_purchases) || 0;
        entry.ad_count += parseInt(row.ad_count) || 0;
      });
    }

    const tiers = await fetchCommissionTiersForDate(
      brandFilter,
      maxDate
    );

    const rows = Array.from(aggregates.values()).map((entry) => {
      const spend = entry.total_ad_spend;
      const revenue = entry.total_revenue;
      const avgRoas = spend > 0 ? revenue / spend : 0;
      const commissionRate = getCommissionRateForRoas(tiers, avgRoas);

      return {
        affiliate_name: entry.affiliate_name,
        total_ad_spend: spend,
        total_revenue: revenue,
        total_commission: spend * commissionRate,
        total_profit:
          entry.total_profit !== null && entry.total_profit !== undefined
            ? entry.total_profit
            : revenue - spend,
        total_purchases: entry.total_purchases,
        ad_count: entry.ad_count,
        avg_roas: avgRoas,
      };
    });

    rows.sort((a, b) => b.total_ad_spend - a.total_ad_spend);

    return rows.slice(0, ALL_TIME_MAX_RESULTS);
  },
  [fetchCommissionTiersForDate, getDateBounds]
);

  // Clear cache function
  const clearCacheData = useCallback(() => {
    if (clearCache) {
      clearCache();
    }
  }, [clearCache]);

  // Note: Client-side processing functions removed - now using JIT database functions

  // JIT: Use database function for all affiliate data processing + include whitelisted affiliates with 0 ads
  const getAllAffiliateData = useCallback(
    async (brand = "all", dateFilter = null) => {
      try {
        setIsLoading(true);
        setError(null);

        // Prepare parameters for database function
        const brandFilter = brand === "all" ? null : brand;
        let startDate = null;
        let endDate = null;
        let useAllTimeLoader = false;

        if (!dateFilter || dateFilter.type === "all") {
          useAllTimeLoader = true;
        } else if (dateFilter.type === "single" && dateFilter.singleDate) {
          startDate = dateFilter.singleDate;
          endDate = dateFilter.singleDate;
        } else if (
          dateFilter.type === "range" &&
          dateFilter.dateRangeStart &&
          dateFilter.dateRangeEnd
        ) {
          startDate = dateFilter.dateRangeStart;
          endDate = dateFilter.dateRangeEnd;
        } else {
          useAllTimeLoader = true;
        }

        let performanceData = [];

        if (useAllTimeLoader) {
          performanceData = await loadAllTimeAffiliateSummary(brandFilter);
        } else {
          try {
            const { data, error: queryError } = await supabase.rpc(
              "get_affiliate_performance_summary",
              {
                brand_filter: brandFilter,
                start_date: startDate,
                end_date: endDate,
                limit_count: 200,
              }
            );

            if (queryError) throw queryError;
            performanceData = data || [];
          } catch (rpcError) {
            const message = rpcError?.message?.toLowerCase?.() || "";
            const isTimeout =
              rpcError?.code === "57014" ||
              message.includes("statement timeout");

            if (isTimeout) {
              console.warn("getAllAffiliateData: RPC timed out, using chunked loader.", {
                brandFilter,
                startDate,
                endDate,
              });
              performanceData = await loadAllTimeAffiliateSummary(brandFilter);
            } else {
              throw rpcError;
            }
          }
        }

        // Step 2: Get all whitelisted affiliates (including those with 0 ads)
        const { data: whitelistData, error: whitelistError } = await supabase
          .from("affiliate_whitelist")
          .select("affiliate_name")
          .eq("is_active", true);

        if (whitelistError) {
          console.warn("⚠️ Could not load whitelist:", whitelistError.message);
        }

        // Step 3: Create a map of performance data
        const performanceMap = new Map();
        (performanceData || []).forEach((row) => {
          performanceMap.set(row.affiliate_name, {
            name: row.affiliate_name,
            totalAdSpend: parseFloat(row.total_ad_spend) || 0,
            totalRevenue: parseFloat(row.total_revenue) || 0,
            totalCommission: parseFloat(row.total_commission) || 0,
            totalProfit: parseFloat(row.total_profit) || 0,
            totalPurchases: parseInt(row.total_purchases) || 0,
            adCount: parseInt(row.ad_count) || 0,
            avgROAS: parseFloat(row.avg_roas) || 0,
            ads: [],
          });
        });

        // Step 4: Add whitelisted affiliates with 0 performance if not already included
        const allAffiliateNames = new Set(performanceMap.keys());
        (whitelistData || []).forEach((whitelistRow) => {
          const affiliateName = whitelistRow.affiliate_name;
          if (!allAffiliateNames.has(affiliateName)) {
            // Add whitelisted affiliate with 0 performance
            performanceMap.set(affiliateName, {
              name: affiliateName,
              totalAdSpend: 0,
              totalRevenue: 0,
              totalCommission: 0,
              totalProfit: 0,
              totalPurchases: 0,
              adCount: 0,
              avgROAS: 0,
              ads: [],
            });
          }
        });

        // Step 5: Convert map to array and sort by spend (performance first, then alphabetical)
        const affiliates = Array.from(performanceMap.values()).sort((a, b) => {
          // First sort by spend (descending)
          if (b.totalAdSpend !== a.totalAdSpend) {
            return b.totalAdSpend - a.totalAdSpend;
          }
          // Then sort alphabetically
          return a.name.localeCompare(b.name);
        });

        console.log(
          `✅ Loaded ${affiliates.length} affiliates (${performanceData?.length || 0} with performance, ${(whitelistData?.length || 0) - (performanceData?.length || 0)} with 0 ads)`
        );

        return {
          affiliates,
          processedRows: affiliates.length,
          skippedRows: 0,
        };
      } catch (err) {
        console.error("❌ useAffiliateData: Error loading data via JIT:", err);
        setError(err.message);
        return {
          affiliates: [],
          processedRows: 0,
          skippedRows: 0,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [loadAllTimeAffiliateSummary]
  );

  // JIT: Get individual affiliate data using robust batch loading
  const getIndividualAffiliateData = useCallback(
    async (
      affiliateName,
      brand = "all",
      dateFilter = null,
      onProgress = null
    ) => {
      try {
        setIsLoading(true);
        setError(null);

        // Prepare filters to match dashboard context
        const brandFilter = brand === "all" ? null : brand;
        let startDate = null;
        let endDate = null;

        if (dateFilter && dateFilter.type !== "all") {
          if (dateFilter.type === "single" && dateFilter.singleDate) {
            startDate = dateFilter.singleDate;
            endDate = dateFilter.singleDate;
          } else if (
            dateFilter.type === "range" &&
            dateFilter.dateRangeStart &&
            dateFilter.dateRangeEnd
          ) {
            startDate = dateFilter.dateRangeStart;
            endDate = dateFilter.dateRangeEnd;
          }
        }

        // OPTIMIZED: Use RPC function that reads from finalized_daily_ad_earnings
        // This ensures commission values are consistent with payout center
        console.log(
          `🚀 Loading finalized ad data for ${affiliateName} via RPC...`
        );

        if (onProgress) {
          onProgress({
            type: "estimate",
            message: `Loading finalized ad data for ${affiliateName}...`,
          });
        }

        const startTime = Date.now();

        // Call the RPC function that reads from finalized_daily_ad_earnings
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "get_individual_affiliate_ads_fast",
          {
            p_affiliate_name: affiliateName,
            brand_filter: brandFilter,
            start_date: startDate,
            end_date: endDate,
            limit_count: 50000, // High limit to get all data
          }
        );

        if (rpcError) {
          console.error(`❌ RPC error, falling back to direct query:`, rpcError);
          throw rpcError;
        }

        const loadTime = Date.now() - startTime;

        // Transform RPC response to match expected field names
        // RPC returns: ad_name, ad_date, brand, affiliate_name, spend, revenue, purchases, roas, commission, campaign_name, adset_name, clicks, impressions
        const allData = (rpcData || []).map(row => ({
          ...row,
          date: row.ad_date, // Map ad_date to date for compatibility
          adSpend: row.spend, // Some components expect adSpend
          // commission is already included from finalized table!
        }));

        console.log(
          `✅ Finalized data loaded: ${allData.length} ad-day records in ${loadTime}ms`
        );

        if (onProgress) {
          onProgress({
            type: "complete",
            total: allData.length,
            batches: 1,
            percent: 100,
            message: `✅ Loading complete! ${allData.length.toLocaleString()} finalized ad records loaded.`,
          });
        }

        let finalData = allData;

        // If no data found with exact affiliate_name match, try pattern matching on finalized table
        if (!finalData || finalData.length === 0) {
          console.log(
            `🔍 No exact match found in finalized data, trying pattern matching...`
          );

          // Query finalized_daily_ad_earnings with pattern matching on ad_name
          let fallbackQuery = supabase
            .from("finalized_daily_ad_earnings")
            .select("*")
            .ilike("ad_name", `%${affiliateName}%`)
            .gt("spend", 0);

          // Apply same filters
          if (brandFilter) {
            fallbackQuery = fallbackQuery.eq("brand", brandFilter);
          }
          if (startDate && endDate) {
            fallbackQuery = fallbackQuery
              .gte("ad_date", startDate)
              .lte("ad_date", endDate);
          }

          fallbackQuery = fallbackQuery
            .order("spend", { ascending: false })
            .limit(50000);

          const { data: fallbackData, error: fallbackError } = await fallbackQuery;

          if (fallbackError) {
            console.error(`❌ Fallback query error:`, fallbackError);
          } else if (fallbackData && fallbackData.length > 0) {
            console.log(
              `✅ Fallback found ${fallbackData.length} ads via pattern matching`
            );
            // Transform to match expected field names
            finalData = fallbackData.map(row => ({
              ...row,
              date: row.ad_date,
              adSpend: row.spend,
            }));
          }
        }

        // Transform to expected format for the modal
        const processedAds = finalData.map((row) => ({
          adName: row.ad_name || "Unknown Ad",
          adSpend: parseFloat(row.spend) || 0,
          revenue: parseFloat(row.revenue) || 0,
          profit: parseFloat(row.profit) || 0,
          purchases: parseInt(row.purchases) || 0,
          roas: parseFloat(row.roas) || 0,
          commission: parseFloat(row.commission) || 0,
          commissionTier: row.commission_tier || "",
          commissionLabel: row.commission_label || "",
          campaignName: row.campaign_name || "",
          adsetName: row.adset_name || "",
          cpa: parseFloat(row.cpa) || 0,
          aov: parseFloat(row.aov) || 0,
          brand: row.brand || "",
          date: row.date,
          affiliateName: row.affiliate_name || affiliateName,
        }));

        return processedAds;
      } catch (err) {
        console.error("Error fetching individual affiliate data:", err);
        setError(err.message);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // OPTIMIZED: Get available brands with caching
  const getAvailableBrands = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use a limited query to get unique brands
      const { data, error } = await supabase
        .from("ad_performance")
        .select("brand")
        .not("brand", "is", null)
        .not("brand", "eq", "")
        .limit(1000);

      if (error) throw error;

      // Get unique brands
      const uniqueBrands = [...new Set(data.map((row) => row.brand))].filter(
        Boolean
      );

      return uniqueBrands;
    } catch (err) {
      console.error("Error fetching available brands:", err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // OPTIMIZED: Get available dates with caching
  const getAvailableDates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Method 1: Try to use SQL aggregation to get unique dates efficiently
      try {
        const { data, error } = await supabase.rpc("get_unique_dates");

        if (!error && data && data.length > 3) {
          return data.map((row) => row.date).sort();
        }
      } catch (rpcError) {
        console.log("RPC function not available, using fallback method");
      }

      // Method 2: Fallback - Get ALL records with pagination to ensure we don't miss any
      const allDates = new Set();
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("ad_performance")
          .select("date")
          .not("date", "is", null)
          .range(from, from + batchSize - 1)
          .order("date", { ascending: true });

        if (error) throw error;

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        // Add dates to Set (automatically handles uniqueness)
        data.forEach((row) => {
          if (row.date) {
            allDates.add(row.date);
          }
        });

        // If we got less than the batch size, we're done
        if (data.length < batchSize) {
          hasMore = false;
        } else {
          from += batchSize;
        }
      }

      const uniqueDatesArray = Array.from(allDates).sort();

      return uniqueDatesArray;
    } catch (err) {
      console.error("Error fetching available dates:", err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    getAllAffiliateData,
    getIndividualAffiliateData,
    getAvailableBrands,
    getAvailableDates,
    isLoading,
    error,
    clearCacheData,
  };
};

export default useAffiliateData;
